import { FunctionalComponent } from 'preact';
import { useEffect, useRef, useCallback } from 'preact/hooks';
import { stylesheet } from 'astroturf';
import { NavNode } from '@accedo/spatial-nav';
import { NavScroll, emitScrollEvent } from '@accedo/virtual-scroll';
import { memo } from 'preact/compat';
import { nav } from '##/init';
import { GENRE_SELECTOR_NAV_ID, MARQUEE_BUTTON_NAV_ID } from '##/utils';
import {
  LiveChannel,
  ChannelGroup,
} from '##/services/tenApi/collections/types';
import { isDeviceIQ3 } from '##/utils/device';
import { useAsyncValue } from '##/utils/hooks';
import { Messengers } from '../Player/utils/Messengers';
import { NavIds } from '../Player/utils/NavIds';
import { LiveGenre } from './LiveGenre';

const PLAYER_LIVE_CAROUSEL_ID = 'live-carousel';

const isPlayerRoute = (): boolean =>
  window.location.pathname.includes('player/');

type GenreSelectorProps = {
  items: ChannelGroup[];
  liveShows?: LiveChannel[];
  carouselId: string;
  isPlayerCarousel?: boolean;
};

const styles = stylesheet`
  @import '##/styles/import.scss';

  .live-genre-selector {
    display: flex;
    margin: pxToVh(14) 0 pxToVh(28) 0;
  }
`;

// needed since SpatialNavigation.setActiveChild() doesn't work for node 1 of the genre rail
const addActiveClassToGenreItem = (idx = 0): void => {
  document
    .getElementById(`${GENRE_SELECTOR_NAV_ID}-${idx}`)
    ?.classList?.add('active');
};

const GenreSelector: FunctionalComponent<GenreSelectorProps> = ({
  items,
  liveShows,
  carouselId,
  isPlayerCarousel,
}) => {
  /*
  TODO: Re-enable post phase 1 launch

  const liveCategoryTealiumEventTimerRef = useRef<NodeJS.Timer>();
  */

  const liveSectionOne = isPlayerCarousel
    ? PLAYER_LIVE_CAROUSEL_ID
    : carouselId;
  const lastNavRef = useRef<string>('');
  const lastActiveGenreRef = useRef<string>('');

  const isIQ3Ref = useAsyncValue(isDeviceIQ3);

  /*
  TODO: Re-enable post phase 1 launch

  const sendLiveCategoryEvent = useCallback(
    (groupName: string, groupIdx: number) => {
      if (liveCategoryTealiumEventTimerRef.current) {
        clearTimeout(liveCategoryTealiumEventTimerRef.current);
        liveCategoryTealiumEventTimerRef.current = undefined;
      }

      liveCategoryTealiumEventTimerRef.current = setTimeout(
        () =>
          TealiumBridge.onActiveGenreChange({
            channelGroup: groupName,
            numberOfChannelGroups: items?.length,
            channelGroupIndex: groupIdx,
          }),
        300,
      );
    },
    [items?.length],
  );
  */

  useEffect(() => {
    if (!liveShows || !items?.length || items?.length <= 1) {
      return (): void => undefined;
    }

    const onNavChange = (navItem: NavNode): void => {
      if (navItem?.id?.includes(carouselId)) {
        const navIdParts = navItem?.id?.split('-');

        const idx = parseInt(navIdParts?.[navIdParts.length - 1], 10);

        const { genreIdx = 0 } = liveShows[idx] || {};

        if (lastActiveGenreRef.current === items?.[genreIdx]?.name) {
          return;
        }

        if (!genreIdx) {
          addActiveClassToGenreItem();
        }

        nav.setActiveChild(
          GENRE_SELECTOR_NAV_ID,
          `${GENRE_SELECTOR_NAV_ID}-${genreIdx}`,
        );

        emitScrollEvent('scroll-to-index', genreIdx, GENRE_SELECTOR_NAV_ID);

        lastActiveGenreRef.current = items?.[genreIdx]?.name;
      } else if (
        navItem.id?.includes(MARQUEE_BUTTON_NAV_ID) ||
        navItem.id === NavIds.ControlButton
      ) {
        const genreSelectorNav = nav.getNode(GENRE_SELECTOR_NAV_ID);

        if (genreSelectorNav && genreSelectorNav.activeChild) {
          const activeGenreNode = nav.getNode(genreSelectorNav.activeChild);

          activeGenreNode?.onInactive?.(activeGenreNode);
        }
      }

      lastNavRef.current = navItem.id || '';
    };

    nav.emitter.on('focus', onNavChange);

    return () => nav.emitter.off('focus', onNavChange);
  }, [Boolean(liveShows), Boolean(items), items?.length, liveShows?.length]);

  const onGenreFocus = useCallback(
    (currentGenre: string, idx: number): void => {
      if (!liveShows?.length) {
        return;
      }
      /*
        TODO: Re-enable post phase 1 launch

        sendLiveCategoryEvent(currentGenre, idx);
      */
      addActiveClassToGenreItem(idx);

      const activeShowNode = nav.getNode(liveSectionOne)?.activeChild;

      const currentLiveShowIdx = parseInt(
        activeShowNode?.split('-')?.pop() as string,
        10,
      );

      const { genre } = liveShows?.[currentLiveShowIdx] || {};

      if (genre?.toLowerCase() === currentGenre.toLowerCase()) {
        return;
      }

      const firstInstanceOfGenre =
        liveShows?.findIndex(
          (show) => show.genre?.toLowerCase() === currentGenre.toLowerCase(),
        ) || 0;

      emitScrollEvent('scroll-to-index', firstInstanceOfGenre, liveSectionOne);

      nav.setActiveChild(
        liveSectionOne,
        `${liveSectionOne}-item-${firstInstanceOfGenre}`,
      );
    },
    [Boolean(liveShows), liveShows?.length],
  );

  return (
    <NavScroll
      className={styles.liveGenreSelector}
      id={GENRE_SELECTOR_NAV_ID}
      orientation={'HORIZONTAL'}
      animate={false}
      navProps={{
        onFocusWithinChange: (isFocusWithin: boolean) => {
          const notLiveCarousel = !lastNavRef.current.includes(liveSectionOne);
          if (isFocusWithin && notLiveCarousel && !isPlayerRoute()) {
            emitScrollEvent('scroll-primary-to-index', 2, 'page-scroll');
          }
        },
      }}
    >
      {items.map((item, idx) => {
        const genreId = `${GENRE_SELECTOR_NAV_ID}-${idx}`;

        return (
          <LiveGenre
            id={genreId}
            key={genreId}
            title={item.name}
            genreIndex={idx}
            onGenreFocus={(...args: [string, number]) => {
              if (isIQ3Ref.current) {
                return;
              }

              onGenreFocus(...args);
            }}
            onGenreClick={(...args: [string, number]) => {
              nav.assignFocus(`${GENRE_SELECTOR_NAV_ID}-${idx}`);

              if (isIQ3Ref.current) {
                onGenreFocus(...args);
              }

              if (
                isPlayerRoute() &&
                !Messengers.fastChannelsShowing$.getCurrentValue()
              ) {
                Messengers.fastChannelsShowing$.send(true);
              }
            }}
          />
        );
      })}
    </NavScroll>
  );
};

const LiveGenreSelector = memo(
  GenreSelector,
  (prevProps, nextProps) =>
    !(
      prevProps.items?.length !== nextProps.items?.length ||
      prevProps.liveShows?.length !== nextProps.liveShows?.length
    ),
);

export { LiveGenreSelector };
